import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { GATE_CODES } from '../components/Shared/GateNotice';
import { identifyUser } from '../utils/monitoring';

// Exported only so the dev-only Studio preview can hand its pages a sample
// user (pages/Professor/StudioPreview.jsx); everything else uses useAuth().
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  // Error reports and analytics follow the signed-in account, by id only.
  // Waits for the first /me answer, and only resets on an actual sign-out,
  // so a logged-out visitor keeps one id across page loads and the visits
  // before signup join up with the account afterwards.
  const userId = user?._id;
  const identified = useRef(null);
  useEffect(() => {
    if (authLoading) return;
    if (userId) identifyUser(userId);
    else if (identified.current) identifyUser(null);
    identified.current = userId || null;
  }, [userId, authLoading]);

  // When a session ends (expired cookie, password changed elsewhere, account
  // removed) the API answers 401 with a code (middleware/authMiddleware.js),
  // but nothing listened: every page just started failing one request at a
  // time. One response hook here covers all ~500 fetch calls without touching
  // them. It only acts when we believed someone was signed in, so a signed-out
  // visitor's 401s change nothing, and only on the codes protect() sets, so a
  // 401 from a wrong password elsewhere doesn't sign anyone out.
  useEffect(() => {
    const originalFetch = window.fetch;
    let redirecting = false;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      // Signing out on purpose: requests still in flight will 401, and that
      // must not bounce the person to "session expired".
      if (url.includes('/api/users/logout')) redirecting = true;
      const res = await originalFetch(...args);
      // "Not yet": an action refused until something is set up (see
      // GATE_CODES). GateNotice offers the fix; the page keeps its own error.
      if ((res.status === 403 || res.status === 400) && userRef.current) {
        const sameOriginApi = url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`);
        if (sameOriginApi) {
          res.clone().json().then((body) => {
            if (GATE_CODES.includes(body?.code)) {
              window.dispatchEvent(new CustomEvent('uf:gate', { detail: { code: body.code, message: body.message } }));
            }
          }).catch(() => {});
        }
      }
      if (res.status === 401 && userRef.current && !redirecting) {
        const sameOriginApi = url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`);
        if (sameOriginApi) {
          const body = await res.clone().json().catch(() => null);
          if (body?.code === 'SESSION_EXPIRED' || body?.code === 'NOT_AUTHENTICATED') {
            redirecting = true;
            setUser(null);
            if (!window.location.pathname.startsWith('/login')) {
              const here = window.location.pathname + window.location.search;
              window.location.replace(`/login?expired=1&returnTo=${encodeURIComponent(here)}`);
            }
          }
        }
      }
      return res;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  // Re-read the account from the server. Needed because two fields on it —
  // `customerProfile.hasPaymentMethod` and
  // `freelancerProfile.stripeOnboardingComplete` — are cached copies of state
  // that actually lives at Stripe, and nothing pushes them back to us. They're
  // only ever repaired as a side effect of calling their /status endpoint
  // (routes/stripeCustomer.js, routes/stripeConnect.js).
  //
  // That produced a bug with a very specific shape: save a card, come back from
  // Stripe, and the checkmark stays unticked until you reload by hand. The
  // ordering is deterministic, not flaky — on the returning page load this
  // provider reads /me *before* the settings section calls /status, so /me
  // hands out the pre-repair `false`, /status then fixes the database and its
  // own local state, and every other reader of `user` is left holding the stale
  // snapshot until something re-reads it. That "something" is this.
  //
  // Deliberately NOT done by making /me itself ask Stripe: /me runs on every
  // page load, and two network round-trips to Stripe on each one would be a
  // heavy price for a field that changes a handful of times per account.
  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch('/api/users/me', { credentials: 'include' });
      if (res.ok) {
        const fresh = await res.json();
        setUser(fresh);
        return fresh;
      }
      // A failed refresh is not a logout. The caller is topping up one or two
      // fields on an account we already have; dropping `user` to null over a
      // blip would sign them out mid-flow, which is far worse than a checkmark
      // that lags one more beat.
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await fetch('/api/users/me', { credentials: 'include' });
        if (res.ok) {
          const me = await res.json();
          setUser(me);
          return me;
        }
        setUser(null);
      } catch {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
      return null;
    }

    // Coming back from Stripe. The settings sections repair their own flag when
    // they mount, but Stripe doesn't always land you there: /onboard accepts a
    // returnPath, so a seller who started from the Post-a-Service studio comes
    // back to that page instead, where nothing asks Stripe anything and the
    // cached flag stays wrong until they happen to visit Settings.
    //
    // Doing it here rather than per-page because the return is a full page load
    // — no SPA navigation to hook — so one check at mount covers every landing
    // spot, present and future. Both flags are cheap to reconcile and the
    // endpoints no-op when there's no Stripe account or customer on file.
    async function syncStripeFlags() {
      const params = new URLSearchParams(window.location.search);
      const returning = params.get('stripe') === 'return' || params.get('stripe') === 'refresh';
      if (!returning) return;
      await Promise.allSettled([
        fetch('/api/stripe/connect/status',  { credentials: 'include' }),
        fetch('/api/stripe/customer/status', { credentials: 'include' }),
      ]);
      await refreshUser();
    }

    if (localStorage.getItem('user')) localStorage.removeItem('user');
    (async () => {
      const me = await fetchUser();
      if (me) await syncStripeFlags(); // authenticated calls; pointless while logged out
    })();
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, setUser, authLoading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook — use anywhere in the app ──────────────────────────────────────────
export function useAuth() {
  return useContext(AuthContext);
}